/**
 * `<CombatApp>`'s pure predicates — the fire-time gates, the prompt-window render gate, and the
 * overwatch ack discriminant. They live here rather than inline in the component for one reason
 * stated repeatedly in their own docs: this repo has no jsdom/headless React renderer, so each is
 * pinned by calling the exact predicate the component calls, against a constructed `EncounterState`
 * (`skirmishKeepAim.test.ts`, `skirmishFireChoices.test.ts`, `promptModalGmGate.test.ts`,
 * `overwatchTakeAck.test.ts`). Every one is re-exported from `../CombatApp`, which is where those
 * tests still import them from.
 */
import { activeAttack, weaponInstanceKey, type EncounterState, type OptInRiderPrompt, type ProjectedEncounterState } from "@lancer-vtt/domain";
import { hasEligibleSkirmishRider, isMoveCheckpointPromptId, liveBackswingReservations, weaponAttackCount } from "@lancer-vtt/rules";
import { resolutionSource, type EncounterIntent, type PromptAnswerKind } from "@lancer-vtt/shared";
import { resolutionAttacker } from "../../../lib/encounter/browserMatchHandle";
import type { BroadcastResolution } from "../../../lib/encounter/remoteEncounterController";

/** `<input>` types that take no typing, so a focused one (a clicked checkbox) is not text entry. */
const NON_TEXT_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

/**
 * Whether a key event originated in a text-entry control — so a shortcut shouldn't fire over typing,
 * and Esc stays with the field (`decisions/0189` layer 0).
 */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  const tag = target.tagName;
  return tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

/**
 * Skirmish keep-aim gate (predicate-gated, NOT
 * always-open): `true` only when the just-fired shot could still open an Aux rider lock — imports
 * the engine wall {@link hasEligibleSkirmishRider}, never re-derives it — and never for
 * a reaction (Overwatch shares `SkirmishChooser` and must always close instantly). Callers must
 * evaluate this on the PRE-shot `state` (before the fired weapon's lock
 * broadcast lands), where the sibling Aux still reads unfired. Exported (not inlined in `fireAt`)
 * so it can be pinned directly with a constructed `EncounterState` — this repo has no
 * jsdom/headless React renderer (see `combatWindowsChoosers.test.ts`'s header for the convention).
 */
export function shouldKeepSkirmishAimForRider(
  state: EncounterState,
  attackerId: string,
  aim: { weaponId: string; reaction?: boolean },
): boolean {
  return !aim.reaction && !!aim.weaponId && hasEligibleSkirmishRider(state, attackerId, aim.weaponId);
}

/**
 * Call-site gate for `fireAt`'s skirmish branch: should it
 * even ASK {@link shouldKeepSkirmishAimForRider} whether to keep the aim armed? `true` only when
 * `attackerId` has NO Skirmish attack lock open yet in the pre-shot `state` — i.e. this fire is
 * genuinely the Skirmish **primary**, the one shot `shouldKeepSkirmishAimForRider`'s own doc comment
 * requires ("Callers must evaluate this on the PRE-shot `state`… where the sibling Aux still reads
 * unfired"). A Skirmish lock only opens once the primary resolves and leaves an eligible unfired Aux
 * (`AttackLock`'s doc comment in `packages/domain/src/encounter/types.ts`), so a lock already being
 * open when a NEW skirmish shot fires can only mean that shot is the rider's own continuation — a
 * state `shouldKeepSkirmishAimForRider` was never contracted to run against. Asking it anyway on an
 * Aux/Aux mount re-derives rider eligibility from a fresh single-element `fired` simulation
 * (`hasEligibleSkirmishRider`) that has no memory of the primary having already fired, so it
 * re-flags the already-fired PRIMARY as still rider-eligible and the chooser stays wrongly armed
 * past the server's real one-rider close (latent on the 4 core Aux/Aux frames; widened to 18-19 of
 * 29 frames — including the Everest starter chassis — once the Flex mount carries a second Aux
 * slot). Mirrors {@link classifySkirmishChooser}'s own `isActiveActor` + {@link activeAttack} read
 * rather than re-deriving it — and, like that classifier, doesn't narrow on `lock.action`: ANY open
 * lock for this actor means the state isn't genuinely pre-shot for a *fresh* Skirmish (RAW forbids
 * starting one while another sequential action is already mid-flight, so this is defensive rather
 * than reachable today). Does NOT touch {@link hasEligibleSkirmishRider} or
 * {@link shouldKeepSkirmishAimForRider} — their single-element-`fired` simulation stays correct for
 * the primary-shot contract they document.
 */
export function isSkirmishPrimaryFire(state: EncounterState, attackerId: string): boolean {
  const turn = state.activation.turn;
  const isActiveActor = turn.phase === "acting" && turn.actor === attackerId;
  return isActiveActor ? activeAttack(state.activation) === null : true;
}

/**
 * `fireAt`'s keep-aim gate for a Skirmish **continuation** (a shot fired while the lock is already
 * open): does `aim`'s weapon still have an attack left after this one? True only for a multi-attack
 * weapon ("can make three attacks at a time", {@link weaponAttackCount}) whose pre-shot lock lists
 * it fewer than `count - 1` times, so the aim stays armed for its next attack. Read on the PRE-shot
 * `state`, like {@link shouldKeepSkirmishAimForRider}; never for a reaction.
 */
export function multiAttackContinues(state: EncounterState, attackerId: string, aim: { weaponId: string; reaction?: boolean }): boolean {
  if (aim.reaction || !aim.weaponId) return false;
  const weapon = state.combatants[attackerId]?.base.weapons?.find((w) => weaponInstanceKey(w) === aim.weaponId);
  const made = activeAttack(state.activation)?.weapons.filter((f) => f === aim.weaponId).length ?? 0;
  return made + 1 < weaponAttackCount(weapon);
}

/**
 * A second call-site gate for `fireAt`'s skirmish branch: is THIS shot a Backswing Cut follow-up
 * riding a live reservation? Reads the SAME `liveBackswingReservations` derivation the row/chooser
 * already reads — never re-derived — matched
 * against the fired weapon exactly like the server's own `stampBackswingFollowup` match. `true` here
 * must short-circuit `shouldKeepSkirmishAimForRider`'s mount-topology keep-aim check (below, in
 * `fireAt`): that check is ignorant of the reservation and, on a mech whose reserved weapon happens
 * to share a mount with an eligible Aux, would otherwise keep the aim armed and invite a further
 * click — but a Backswing follow-up's free-ness is a ONE-SHOT server match, not a genuine Skirmish
 * primary opening a fresh mid-attack lock, so that further shot would arrive as an unauthorized plain
 * attack and bounce. Exported alongside its siblings for the same reason: no jsdom/headless React
 * renderer in this repo, so the decision is pinned by calling the exact predicate `fireAt` calls.
 */
export function isBackswingReservationFire(state: EncounterState, attackerId: string, weaponId: string): boolean {
  const attacker = state.combatants[attackerId];
  if (!attacker) return false;
  return liveBackswingReservations(attacker, state.activation.turn).some((s) => s.weapon === weaponId);
}

/**
 * `fireAt`'s skirmish-branch fire-time resolution funnel — the
 * ONE place that turns the armed draft's `aim.choices` into the explicit wire value
 * `buildAttackIntent` sends. The engine is **fail-closed** (a missing `optIn:` answer DECLINES),
 * so a checkbox that RENDERS ticked must actually cause an explicit `"true"` on the wire — there is
 * no live mid-resolve opt-in prompt to fall back to the way ammo `choose` prompts have one. This
 * function:
 *
 *   1. Resolves EVERY rider projected for the armed weapon (`ProjectedEncounterState.optInRiders`,
 *      the SAME `weaponInstanceKey` lookup `SkirmishChooser`'s `armedOptInRiders` used to render the
 *      checkboxes) to an explicit `"true"`/`"false"` — `aim.choices[r.key]` when the player touched
 *      it, else `String(r.default)` (the authored default the UI pre-filled but never assumed).
 *   2. Strips any `optIn:` key in `aim.choices` that is NOT among those currently-projected riders —
 *      a stale answer left over from a previously-armed weapon (the player can switch weapons while
 *      armed; `aim.choices` isn't cleared on a weapon swap).
 *   3. Leaves every non-`optIn:` key (the ammo `choose` answers) untouched, passing through exactly
 *      as `aim.choices` already carried it — this function only ever ADDS/overrides `optIn:` keys and
 *      DROPS stale ones, never touches the ammo family.
 *
 * Exported (not inlined in `fireAt`) for the same reason as its siblings above: no jsdom/headless
 * React renderer in this repo, so this is pinned directly with a constructed `EncounterState` rather
 * than rendering `SkirmishChooser` and driving a click through it.
 */
export function resolveSkirmishFireChoices(
  state: EncounterState,
  attackerId: string,
  aim: { weaponId: string; choices?: Record<string, string> },
): Record<string, string> {
  const attacker = state.combatants[attackerId];
  const chosenWeapon = attacker ? (attacker.base.weapons ?? []).find((w) => weaponInstanceKey(w) === aim.weaponId) : undefined;
  const projectedState: ProjectedEncounterState = state;
  const armedOptInRiders: OptInRiderPrompt[] = chosenWeapon
    ? (projectedState.optInRiders?.[attackerId]?.[weaponInstanceKey(chosenWeapon)] ?? [])
    : [];
  const riderKeys = new Set(armedOptInRiders.map((r) => r.key));
  const resolved: Record<string, string> = {};
  for (const [k, v] of Object.entries(aim.choices ?? {})) {
    if (k.startsWith("optIn:") && !riderKeys.has(k)) continue; // stale rider from a previously-armed weapon
    resolved[k] = v;
  }
  for (const r of armedOptInRiders) {
    resolved[r.key] = aim.choices?.[r.key] ?? String(r.default);
  }
  return resolved;
}

/**
 * The floating `PromptModal` window's render gate. The GM's socket receives `encounter_prompt` for
 * EVERY cross-actor decision (window recipients = decider slot + every GM slot,
 * `backend/src/encounterRoom.ts`'s `sendDecisionPrompt`) — a narrower recipient list starves the
 * GM's own `RemoteEncounterController.prompt_` and makes a GM's COMMS-card click a silent no-op. So
 * `encounter.prompt` populates for every GM, decider or not — this predicate decides whether the
 * floating WINDOW should actually RENDER that prompt, or stay hidden in favor of the COMMS card:
 *
 *   - **suppress** only when the viewer is a GM who is NOT among the deciders (`whoSlotIds` omits `actorId`)
 *     AND the prompt is options-type (`answerKind !== "direction"`) — a GM merely resolving
 *     someone else's reaction (Brace/Overwatch/a `0096` offer) has the card instead. A `"cell"` or
 *     `"combatant"` prompt counts: the bystander answers it from the card's list, whose hovered
 *     option highlights its space or unit (`board-pick-prompts` 4.1).
 *   - **never suppress** a GM who IS the decider (e.g. an NPC-side Brace where the deciding
 *     combatant's controllers include the GM's own slot) — `whoSlotIds` contains `actorId` there, so the
 *     window opens exactly like for any other decider.
 *   - **never suppress** a `"direction"`-type prompt — the card has no click-a-cell affordance yet
 *     (a deferred follow-on), so the GM keeps the floating board-click
 *     window as a backstop for that answer kind regardless of who's deciding.
 *   - **never suppress** when `whoSlotIds` is absent (a bare/test room, or an NPC decider with no
 *     controller slot at all) — there's no slot to compare against, so the window is unaffected.
 *   - **never suppress** a **same-actor** prompt (the decider IS the acting source — an ammo /
 *     variable-damage-type / Lock-On `choose` on your *own* attack, `prompt.who ===
 *     resolutionSource(prompt.intent)`). `sendDecisionPrompt` delivers those to the acting socket
 *     ONLY and never mints a COMMS card, so there is no card to defer to. Without this carve-out a
 *     GM driving a combatant owned by a *different* slot (a PC mech's Skirmish) would see the
 *     floating window suppressed by the membership clause below AND no card — the
 *     prompt would silently vanish (Walking Armory's ammo `choose` never
 *     appeared when the GM fired the PC mech). Only a CROSS-actor prompt (Brace/Overwatch/a `0096`
 *     offer, decider ≠ source) has a card the GM bystander may defer to.
 *
 * Exported as a pure predicate (mirrors {@link shouldKeepSkirmishAimForRider} just above) since
 * there's no jsdom/headless renderer in this repo to mount `CombatApp` and drive the gate through
 * a real render — see `skirmishKeepAim.test.ts`'s header for the same convention.
 */
export function suppressPromptModalForGmBystander(
  prompt: { answerKind?: PromptAnswerKind; whoSlotIds?: string[]; who?: string; intent?: EncounterIntent } | null,
  role: "gm" | "player" | null,
  actorId: string | null,
): boolean {
  if (prompt === null) return false;
  // Same-actor prompts have no card fallback — the floating window must always show (`who`/`intent`
  // are always present on a live `PromptRequest`; a bare predicate fixture omitting them falls
  // through to the cross-actor gate below unchanged).
  if (prompt.who !== undefined && prompt.intent !== undefined && prompt.who === resolutionSource(prompt.intent)) {
    return false;
  }
  if (role !== "gm" || prompt.answerKind === "direction" || prompt.whoSlotIds === undefined) return false;
  return actorId === null || !prompt.whoSlotIds.includes(actorId);
}

/**
 * Whether the floating prompt window opens for a prompt this client holds. Not for a GM bystander's
 * copy ({@link suppressPromptModalForGmBystander}), and not for a `"cell"` or `"combatant"` prompt:
 * the decider answers those on the board, and the board banner carries the question
 * (developer ruling 2026-09-30, `board-pick-prompts`).
 */
export function opensPromptWindow(prompt: { answerKind?: PromptAnswerKind } | null, suppressed: boolean): boolean {
  return prompt !== null && !suppressed && prompt.answerKind !== "cell" && prompt.answerKind !== "combatant" && prompt.answerKind !== "combatants";
}

/**
 * Whether a prompt is one the table waits on, so Esc cannot dismiss it (`decisions/0189` point 6):
 * its decider is not the source of the resolution that raised it — the same cross-actor split
 * {@link suppressPromptModalForGmBystander} and the room's `sendDecisionPrompt` key on. Reaction
 * offers (Brace, Intercede), move-checkpoint offers (Overwatch) and a direction pick asked of
 * another unit's controller are required; a choice raised by the viewer's own action (an ammo
 * `choose`, Hunter's Lunge's magnitude, an attacker-chosen push) is dismissible. A checkpoint offer
 * is named explicitly as well: its ✕ is a server decline, so Esc must never reach it.
 */
export function promptIsRequired(prompt: { who: string; promptId: string; intent: EncounterIntent }): boolean {
  return isMoveCheckpointPromptId(prompt.promptId) || prompt.who !== resolutionSource(prompt.intent);
}

/**
 * The `PromptModal`'s decider line — resolve `prompt.who` (a COMBATANT id) to the display name the
 * modal prints as "For <name>", or `undefined` when it resolves to nothing.
 *
 * **The same resolution the attacker's `ReactionWaitBanner` shows**, which reads the decider's name
 * off the server-built `GameDecisionMessage.names` map (`deriveResolutionWait` →
 * `decision.names[decision.who]`, itself `combatant.name` at broadcast time,
 * `encounterRoomCore.ts`'s `combatantNames`). The floating window rides `/ws/encounter` and gets no
 * such map, so it resolves the id against the live encounter state the component already holds —
 * the same `state.combatants[id]?.name` the room maps from.
 *
 * **Returns `undefined` rather than the raw id** (`CombatApp`'s own `nameOf` falls back to the id;
 * this deliberately does not). A prompt whose decider this client cannot see is a fog/projection
 * case, and "For def2" is worse than no line at all — the host omits the prop and the line does not
 * render. `who` is always present on a live `PromptRequest`; the optional is for predicate fixtures.
 *
 * Pure, for the same reason every predicate in this file is: there is no jsdom/headless renderer
 * here to mount `CombatApp` and read the rendered line (`promptDeciderName.test.ts`).
 */
export function promptDeciderName(
  state: Pick<EncounterState, "combatants"> | null,
  prompt: { who?: string } | null,
): string | undefined {
  if (state === null || prompt?.who === undefined) return undefined;
  return state.combatants[prompt.who]?.name;
}

/**
 * The `PromptModal`'s question source — the display name of the combatant whose action raised the
 * prompt ({@link resolutionSource} of the echoed intent), for "Brace against `<name>`'s hit?".
 * `undefined` when it is the decider itself (a same-actor prompt has no "against"), when the intent
 * names no source (GM damage), or when this client cannot see it — the question then reads `Brace?`.
 * Same no-raw-id rule as {@link promptDeciderName}.
 */
export function promptSourceName(
  state: Pick<EncounterState, "combatants"> | null,
  prompt: { who?: string; intent?: EncounterIntent } | null,
): string | undefined {
  if (state === null || prompt?.intent === undefined) return undefined;
  const source = resolutionSource(prompt.intent);
  if (source === "" || source === prompt.who) return undefined;
  return state.combatants[source]?.name;
}

/**
 * The take-flow ack effect's discriminant (`overwatch-brace-interaction-audit` Task 1): does
 * `res` — the latest arrived resolution — mean OUR watcher's sibling shot just landed? Exported as
 * a pure predicate (mirrors {@link suppressPromptModalForGmBystander} just above) since there's no
 * jsdom/headless renderer in this repo to mount `CombatApp` and drive the effect through a real
 * render — see `skirmishKeepAim.test.ts`'s header for the same convention.
 *
 * **Deliberately NOT `res?.actorId === actorId`** (the original, false, comparison this predicate
 * replaces). `res.actorId` is the resolution's ATTRIBUTION slot (`resolutionAttribution`,
 * `backend/src/encounterRoom.ts`) — the socket that gets to answer, not necessarily the one that
 * fired. For a GM-controlled NPC watcher with no `controllerSlotId`, attribution for the sibling
 * Brace round-trip its own shot raises falls back to `senderSlot`, the ANSWERING PLAYER — so on the
 * GM's own client `res.actorId` is the player's slot and never matches the GM's `actorId`, and the
 * old comparison silently never fires (`overwatch-brace-interaction-audit`, assertion (f)). Uses
 * {@link resolutionAttacker} instead, which reads the resolution's own recorded `to_hit`/
 * `miss_chance` step and names the COMBATANT that attacked — independent of who it's attributed to
 * on the wire — and compares it against the watcher COMBATANT id captured at `startOverwatchTake`
 * time.
 */
export function overwatchTakeAckMatches(res: BroadcastResolution | null, watcherId: string): boolean {
  return res !== null && resolutionAttacker(res) === watcherId;
}
